// Modern Vite Entry Point for Global Mining Risk Application
// Refactored modular architecture with service coordination

// Setup global libraries
import './config/globalSetup.js';

// Import new service architecture
import { appInitializer } from './services/appInitializer.js';
import { componentManager } from './services/componentManager.js';
import { eventCoordinator } from './services/eventCoordinator.js';
import { filterManagerInstance } from './services/filterManager.js';
import { mapService } from './services/mapService.js';

class Application {
  constructor() {
    this.isInitialized = false;
  }

  async initialize() {
    try {
      console.log('🚀 Initializing Global Mining Risk application (Modern)');

      // Perform health check
      const healthResult = await appInitializer.performHealthCheck();
      if (!healthResult.healthy) {
        console.warn('⚠️ Proceeding with initialization despite API health check failure');
      }

      // Initialize URL handling
      appInitializer.initializeUrlHandling();

      // Initialize core services
      await appInitializer.initializeServices();

      // Load risk data
      await appInitializer.loadRiskData();

      // Initialize map
      await appInitializer.initializeMap();

      // Initialize UI components
      componentManager.initializeComponents();

      // Set up event coordination
      eventCoordinator.initialize(componentManager, filterManagerInstance);

      // Load initial data and update components
      const initialData = await appInitializer.loadInitialData();
      mapService.updateMarkers(initialData.mines);
      componentManager.updateMapWithMines(initialData.mines);
      await componentManager.loadFilterOptions();

      this.isInitialized = true;
      console.log('✅ Application initialized successfully');

      // Hide loading overlay
      const loadingIndicator = document.getElementById('loadingIndicator');
      if (loadingIndicator) {
        loadingIndicator.style.display = 'none';
      }

      // Show welcome splash after everything is loaded
      const welcomeSplash = componentManager.getComponent('welcomeSplash');
      if (welcomeSplash) {
        // Small delay to ensure smooth transition from loading
        setTimeout(() => {
          welcomeSplash.show();
        }, 500);
      }

      // Dispatch ready event
      const event = new CustomEvent('applicationReady');
      document.dispatchEvent(event);

    } catch (error) {
      console.error('❌ Failed to initialize application:', error);
      this.showError('Failed to initialize application. Please refresh the page.');
    }
  }

  // Public API methods
  getComponent(name) {
    return componentManager.getComponent(name);
  }

  getService(name) {
    return appInitializer.getServices()[name];
  }

  // Expose all components for external access
  get components() {
    return componentManager.getAllComponents();
  }

  isReady() {
    return this.isInitialized;
  }

  showError(message) {
    componentManager.showError(message);
  }

  destroy() {
    console.log('🧹 Destroying application...');

    // Destroy event coordinator
    eventCoordinator.destroy();

    // Destroy all components
    componentManager.destroyAllComponents();

    // Clean up map service
    if (mapService) {
      mapService.destroy();
    }

    this.isInitialized = false;
    console.log('✅ Application destroyed');
  }
}

// Initialize application when DOM is ready
let app = null;

function initializeApp() {
  app = new Application();
  app.initialize();

  // Export globally for debugging
  window.app = app;

  // Export modal components globally for easy access
  window.contactUs = app.getComponent('contactUs');
  window.premiumMembers = app.getComponent('premiumMembers');

  console.log('✅ Global exports set:', {
    contactUs: !!window.contactUs,
    premiumMembers: !!window.premiumMembers
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}

// Export for potential external use
export { Application };